// src/components/VaccineSaleList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Spinner,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Flex,
  Grid,
  GridItem,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { Search, Clear } from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";
import CustomDatePicker, { convertToThDate } from "./CustomDatePicker";

const getDefaultFilters = () => {
  const today = new Date();
  const startDate = new Date();
  startDate.setDate(today.getDate() - 15);
  return {
    StartDate: startDate,
    EndDate: today,
    PickupPoint: "",
    FarmerName: "",
    TransactionID: "",
  };
};

const formatNumber = (value) => Number(value || 0).toLocaleString("th-TH");

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// รูปแบบการ์ดสำหรับจอเล็ก ที่ตารางจะแน่นเกินไป
const MobileSaleList = ({ data, onOpenDetail }) => (
  <VStack spacing={4} w="100%">
    {data.map((row) => (
      <Box
        key={row.TransactionID}
        w="100%"
        p={{ base: 3, sm: 4 }}
        borderWidth={1}
        borderRadius="lg"
        boxShadow="sm"
      >
        <VStack align="start" spacing={3}>
          <Flex
            w="100%"
            justify="space-between"
            align="flex-start"
            gap={2}
            minW={0}
          >
            <VStack align="start" spacing={1} minW={0} flex="1">
              <Text fontWeight="bold" fontSize="md" noOfLines={2}>
                {row.TransactionID}
              </Text>
              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                {row.FarmerName || "-"}
              </Text>
            </VStack>
            <IconButton.ViewIcon
              size="sm"
              aria-label="ดูรายละเอียด"
              title="ดูรายละเอียด"
              flexShrink={0}
              onClick={() => onOpenDetail(row.TransactionID)}
            />
          </Flex>

          <Box w="100%" pt={2} borderTopWidth="1px" borderColor="gray.200">
            <Grid
              templateColumns="auto 1fr"
              gapX={2}
              gapY={1}
              fontSize="sm"
              color="gray.600"
            >
              <GridItem fontWeight="medium">วันที่จำหน่าย:</GridItem>
              <GridItem>{convertToThDate(row.RequestDate)}</GridItem>

              <GridItem fontWeight="medium">จำนวนรายการ:</GridItem>
              <GridItem>{formatNumber(row.ItemCount)}</GridItem>

              <GridItem fontWeight="medium">จำนวนขวด:</GridItem>
              <GridItem>{formatNumber(row.TotalQuantity)}</GridItem>

              <GridItem fontWeight="medium">ยอดรวมเงิน:</GridItem>
              <GridItem>{formatCurrency(row.TotalAmount)} บาท</GridItem>
            </Grid>
          </Box>
        </VStack>
      </Box>
    ))}
  </VStack>
);

const VaccineSaleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const today = new Date();

  // ต่ำกว่า lg ตารางจะแน่นเกินไป จึงสลับไปใช้การ์ดแทน
  const isMobile = useBreakpointValue(
    { base: true, lg: false },
    { ssr: false }
  );

  const [sales, setSales] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState(getDefaultFilters);

  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
      minWidth: "230px",
      wrap: true,
    },
    {
      name: "วันที่จำหน่าย",
      // เรียงลำดับด้วยค่าดิบเพื่อให้เรียงตามเวลาจริง ไม่ใช่ตามตัวอักษร
      selector: (row) => row.RequestDate || "",
      format: (row) => convertToThDate(row.RequestDate),
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "ชื่อเกษตรกร",
      selector: (row) => row.FarmerName,
      sortable: true,
      minWidth: "200px",
      wrap: true,
    },
    {
      name: "จำนวนรายการ",
      selector: (row) => Number(row.ItemCount || 0),
      format: (row) => formatNumber(row.ItemCount),
      sortable: true,
      right: true,
      minWidth: "120px",
      hide: 1280,
    },
    {
      name: "จำนวนขวด",
      selector: (row) => Number(row.TotalQuantity || 0),
      format: (row) => formatNumber(row.TotalQuantity),
      sortable: true,
      right: true,
      minWidth: "110px",
    },
    {
      name: "ยอดรวมเงิน (บาท)",
      selector: (row) => Number(row.TotalAmount || 0),
      format: (row) => formatCurrency(row.TotalAmount),
      sortable: true,
      right: true,
      minWidth: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccinesale/${row.TransactionID}`)}
          label="รายละเอียด"
          size="sm"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "140px",
    },
  ];

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchSales = async (filters = filterData) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineSaleOrdersList",
        data: {
          StartDate: filters.StartDate || "",
          EndDate: filters.EndDate || "",
          PickupPoint: filters.PickupPoint || "",
          FarmerName: filters.FarmerName?.trim() || "",
          TransactionID: filters.TransactionID?.trim() || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setSales(response.VaccineSaleOrdersList || []);
      } else {
        setSales([]);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
      setSales([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchWarehouses();
      fetchSales(getDefaultFilters());
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    fetchSales(filterData);
  };

  const handleClearSearch = () => {
    const resetFilters = getDefaultFilters();
    setFilterData(resetFilters);
    fetchSales(resetFilters);
  };

  const handleCreateNew = () => {
    navigate("/vaccinesale/new");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="start">
        <StandardHeader
          title="รายการจำหน่ายวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        <Box
          w="100%"
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          p={{ base: 3, md: 4 }}
        >
          <StandardHeader
            title="ค้นหาข้อมูลการจำหน่ายวัคซีน"
            bgColor="iBlue.600"
            icon={faSearch}
          />
          <Grid
            w="100%"
            minW={0}
            gap={3}
            mt={3}
            templateColumns={{
              base: "1fr",
              md: "auto minmax(0, 1fr)",
              lg: "auto repeat(2, minmax(0, 1fr))",
            }}
          >
            {/* ช่องวันที่กว้างตามเนื้อหา จึงจับคู่ไว้ในช่องเดียวกันให้อยู่ติดกัน */}
            <Flex gap={3} wrap="wrap" align="flex-end">
              <FormControl w="auto">
                <FormLabel
                  fontSize={{ base: "sm", md: "md" }}
                  color="gray.600"
                  mb={1}
                >
                  วันที่เริ่มต้น
                </FormLabel>
                <CustomDatePicker
                  name="StartDate"
                  value={filterData.StartDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  maxDate={today}
                />
              </FormControl>

              <FormControl w="auto">
                <FormLabel
                  fontSize={{ base: "sm", md: "md" }}
                  color="gray.600"
                  mb={1}
                >
                  วันที่สิ้นสุด
                </FormLabel>
                <CustomDatePicker
                  name="EndDate"
                  value={filterData.EndDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  minDate={minDateForEndDate}
                  maxDate={today}
                />
              </FormControl>
            </Flex>

            <FormControl>
              <FormLabel
                fontSize={{ base: "sm", md: "md" }}
                color="gray.600"
                mb={1}
              >
                จุดจำหน่าย
              </FormLabel>
              <Select
                name="PickupPoint"
                value={filterData.PickupPoint}
                onChange={handleInputChange}
                placeholder="เลือกจุดจำหน่าย (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel
                fontSize={{ base: "sm", md: "md" }}
                color="gray.600"
                mb={1}
              >
                เลขที่เอกสาร
              </FormLabel>
              <Input
                name="TransactionID"
                value={filterData.TransactionID}
                onChange={handleInputChange}
                placeholder="ค้นหาเลขที่เอกสาร"
                colorScheme="iBlue"
              />
            </FormControl>

            <FormControl>
              <FormLabel
                fontSize={{ base: "sm", md: "md" }}
                color="gray.600"
                mb={1}
              >
                ชื่อเกษตรกร
              </FormLabel>
              <Input
                name="FarmerName"
                value={filterData.FarmerName}
                onChange={handleInputChange}
                placeholder="ค้นหาชื่อเกษตรกร"
                colorScheme="iBlue"
              />
            </FormControl>

            {/* ปุ่มค้นหาอยู่ในช่องสุดท้ายของกริด เพื่อไม่ให้เปลืองอีกหนึ่งแถว */}
            <Flex
              gap={3}
              align="flex-end"
              justify="flex-end"
              gridColumn={{ md: "span 2" }}
            >
              <Clear onClick={handleClearSearch} />
              <Search onClick={handleSearch} isLoading={isLoading} />
            </Flex>
          </Grid>
        </Box>

        <Flex w="100%" justify="flex-end">
          <IconButton.Add
            onClick={handleCreateNew}
            label="เพิ่มรายการใหม่"
            w={{ base: "100%", sm: "auto" }}
          />
        </Flex>

        {isLoading ? (
          <Flex justify="center" w="100%" py={10}>
            <Spinner size="xl" />
          </Flex>
        ) : sales.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลใบจำหน่ายวัคซีนตามเงื่อนไขที่ค้นหา
          </Text>
        ) : isMobile ? (
          <MobileSaleList
            data={sales}
            onOpenDetail={(transactionId) =>
              navigate(`/vaccinesale/${transactionId}`)
            }
          />
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={sales}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineSaleList;
